import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Policy } from '../../../../../models/policy/Policy';
import {
  INSTALLMENT_TYPES,
  PAYMENT_STATUS_STATES,
  POLICY_REVIEW_STATES,
  POLICY_STATUS_OPTIONS,
  PRODUCTS,
  UPLOAD_IMAGE_ENDPOINTS,
  UPLOADABLE_DOCUMENT_TYPES,
} from '../../../../../resources/enums';
import { IDocument, IEnum, ITask } from '../../../../../resources/interfaces';
import {
  capitalizeSentence,
  formatCurrencyAmount,
  getFormattedDate,
  getFormattedLeadSource,
  getFormattedPaymentChannel,
  getFormattedPhoneNumber,
  getInstallmentTypeFromKey,
  getPaystackConfig,
  getPhoneNumberFromShortCodeFormat,
  getVehicleBodyTypeFromString,
  getVehicleProtectionFromKey,
  isOfflinePayment,
  removeUnderscoresAndCapitalize,
  sentenceCase,
  showToast,
  sortArrayByAlphabets
} from '../../../../../utils/miscFunctions';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ReactComponent as CheckIcon } from './imgs/check-success.svg';
import {
  fetchPolicyBlockers,
  getCommissionsByApplicationNumber,
  getPayoutByPolicyNumber,
  getPolicy,
  getPolicyClaims,
  makeSelectedPaymentsForAPolicy,
  resolveFinancials,
  PolicyBlocker,
} from '../../../../../api/requests';
import useModal from '../../../../../hooks/modalHook';
import useUserAccount from '../../../../../hooks/userAccountHook';
import MessagePrompt from '../../../../../components/elements/MessagePrompt';
import { MdChangeCircle } from 'react-icons/md';
import clsx from 'clsx';
import { EllipsisVerticalIcon, XMarkIcon } from '@heroicons/react/24/solid';
import PopOnMe from '../../../../../components/elements/poponme';
import useRoute from '../../../../../hooks/routesHook';
import GTable, { ITableAction } from '../../../../../components/elements/GTable/GTable';
import ToolTip from '../../../../../components/ToolTip';
import PolicyUpdates from '../PolicyUpdates';
import { IPolicyUpdate } from '../../../../../models/policy/IPolicy';
import IconStatus from '../../../../../components/elements/IconStatus';
import RefereePhoneVerificationModal from '../RefereePhoneVerification';
import BackButton from '../../../../../components/BackButton';
import usePayment from '../../../../../hooks/paymentHook';
import { PaystackProps } from 'react-paystack/dist/types';
import NotFoundComponent from '../../../../../components/NotFoundComponent';
import PolicyStatus from '../../../../../components/elements/PolicyStatus';
import PaymentStatus from '../../../../../components/PaymentStatus';
import TabContent from '../../../../../components/tabs/TabContent';
import { SUCCESS_PAYMENT_STATUS_COLOR } from '../../../../../resources/constants';
import { Claim } from '../../../../../models/claim/Claim';
import ClaimStages from '../../../../../components/elements/ClaimStages';
import { TwoStateVerifiedCheck } from '../../../../../components/statuses';
import useLayoutHook from '../../../../../hooks/layoutHook';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Tooltip from '../../../../../components/ToolTip';
import DiscountInfoModal from '../DiscountInfoModal';
import { FaEye } from 'react-icons/fa';
import { IoIosLink } from "react-icons/io";
import { IPayment } from '../../../../../models/payment/IPayment';
import IconAction from '../../../../../components/elements/GTable/InGTableComponents/IconAction';
import { TbClipboardCopy } from 'react-icons/tb';
import ActionsPopOnMe from '../../../../../components/ActionsPopOnMe';
import usePolicy from '../../../../../hooks/policyHook';
import ImageGallery from '../../../../../components/elements/imageViewer/ImageGallery';
import { BsImageFill } from 'react-icons/bs';
import VehicleAnalysisCard from '../../../vehicle-verification/VehicleAnalysisCard';
import { ClipboardCheck, Building2, Car, Shield, CheckCircle, Clock, XCircle, User, Percent, Repeat, Calendar, CreditCard, FileText, Info, PersonStanding, PlusCircle, RotateCw, Download, FilePlus, BadgeCheck, Share2, MessageSquarePlus, Camera, FileMinus, Edit, ReceiptText, FileCheck, Paperclip, Send, Eye, Pause, FileSearch, AppWindow, Upload, File, CheckCircle2, XCircleIcon, LinkIcon, StickyNote, CameraIcon, Table, Trash, Archive, SearchIcon, Edit2, ChevronRight, AlertTriangle } from 'lucide-react';
import DocumentDownloadCard from '../../../../../components/cards/DocumentDownloadCard';
import TabNav, { ITab } from '../../../../../components/tabs/TabNav';
import { PopOver } from '../../../../../components/elements/PopOver';
import PaymentAnalysis from '../../../../../components/PaymentAnalysis';
import { Commission } from '../../../../../models/commission/Commission';
import { Payout } from '../../../../../models/payout/Payout';
import usePayouts from '../../../../../hooks/payoutHook';
import useSettlements from '../../../../../hooks/settlementHook';
import { Settlement } from '../../../../../models/settlement/Settlement';
import { useClaim } from '../../../../../hooks/claimHook';
import { Vehicle } from '../../../../../models/vehicle/Vehicle';
import PolicyDetailsDrawerContent from './components/PolicyDetailsDrawerContent';
import Button from '../../../../../components/form-control/Button';
import { useDispatch, useSelector } from 'react-redux';
import { getEnumsFromState } from '../../../../../states/slices/resourceSlice';
import { CLEAR_QUOTE_WIDGET_PAYLOAD } from '../../../../../states/slices/quoteWidgetSlice';
import AddVehicleForm from './components/AddVehicleForm';
import ShowPendingInfo from '../../../../../components/elements/ShowPendingInfo';
import ActionConfirmation from '../../../../../components/elements/ActionConfirmation';
import Loader from '../../../../../components/Loader';

interface IPolicyDetailsProps {
  previousPath: string;
}

const PolicyDetails = (props: IPolicyDetailsProps): JSX.Element => {
  const [policy, setPolicy] = React.useState<Policy>();
  const [payout, setPayout] = React.useState<Payout>();
  const [claims, setClaims] = React.useState<Claim[]>([]);
  // Pending Requirements: derived blocker list (Awaiting Payment, Missing KYC, …).
  // Drives the warning banner on the detail page and overrides the badge label.
  const [blockers, setBlockers] = React.useState<PolicyBlocker[]>([]);
  const [isApproving, setIsApproving] = React.useState<boolean>(false);
  const pageRef = useRef<HTMLDivElement>(null);
  const { handleCompletePayout, PayoutModal } = usePayouts()
  const { handleCompleteSettlement, SettlementModal } = useSettlements({});
  const paystackConfig: PaystackProps = getPaystackConfig(policy);
  const enums = useSelector(getEnumsFromState);
  const { createClaim, ClaimModal } = useClaim();
  const { handleReloadPolicyVehiclePhotos, renewMotorPolicy, applyDiscount, handleEditVehicleInformationModal, showCreateTaskModal, handleDeletePolicyByNumber, showEditPaymentModal, showMarkPolicyAsCompleteModal, uploadPolicyDocument, handleApplyLoanToPolicy, handleApprovePolicy, handleSubmitForReview, handleSubmitForApproval, handleDeclinePolicy, handleCancelPolicy, handleRequestToCancelPolicy, handleSetPolicyOnHold, assignPolicyToBroker, handleGeneratePaymentReceipt, setPolicyToDueLater, assignPolicyManager, showDownloadPolicyDetailsModal, getPolicyVehiclePhotos, sendAgreementFormToCustomer, PolicyModalComponent,
    markAsRenewed, openPictureModal, sendReviewLinkToCustomer, runPaymentAnalysis, showMotorPaymentLinkModal, archiveAPolicy, handleGeneratePolicyDocuments, handleGenerateSaleForAPolicy, showGenerateStickerForm, manuallyVerifyVIN, completeAllPayments } = usePolicy();
  const [loading, setLoading] = React.useState<boolean>(false);
  const [refresh, setRefresh] = useState<boolean>(false);
  const [showUpdates, setShowUpdates] = useState<boolean>(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const { ModalComponent, updateModalStates, closeModal } = useModal();
  const [selectedPayments, setSelectedPayments] = useState<any>([]);
  const [settlement, setSettlement] = useState<Settlement>()
  const [activeTab, setActiveTab] = useState('payments');
  const [searchQuery, setSearchQuery] = useState('');
  const params = useParams();
  const navigate = useNavigate();
  const { user } = useUserAccount();
  const dispatch = useDispatch();
  const { getDashboardBase } = useRoute();
  const { screenWidth } = useLayoutHook();
  const [commission, setCommission] = useState<Commission>();
  const policyId = params.policyId;
  const hasPageHistory = window.history.length > 1
  const queryClient = useQueryClient();
  const invalidatePolicy = () => queryClient.invalidateQueries({ queryKey: ['fetchPolicy'] });
  const handleInvalidatePayoutsQueries = () => queryClient.invalidateQueries({ queryKey: ['fetchSinglePayout'] });
  const handleInvalidateCommissionsQueries = () => queryClient.invalidateQueries({ queryKey: ['fetchCommissions'] });
  const [registrationNumbers, setRegistrationNumbers] = useState<string[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[] | null>([]);
  const [selectedVehicleNumber, setselectedVehicleNumber] = useState<string>('');
  const selectVehicleId = policy?.vehicles?.filter((vehicle: Vehicle) => vehicle.registrationNumber === selectedVehicleNumber)[0]?.id || undefined;

  const documentList = [
    UPLOADABLE_DOCUMENT_TYPES.DRIVER_LICENSE_FRONT,
    UPLOADABLE_DOCUMENT_TYPES.DRIVER_LICENSE_BACK,
    UPLOADABLE_DOCUMENT_TYPES.ID_CARD,
    UPLOADABLE_DOCUMENT_TYPES.INSURANCE_STICKER,
    UPLOADABLE_DOCUMENT_TYPES.INSURANCE_CERTIFICATE,
    UPLOADABLE_DOCUMENT_TYPES.CONTRACT_SUMMARY,
    UPLOADABLE_DOCUMENT_TYPES.PASSPORT_PHOTO,
    UPLOADABLE_DOCUMENT_TYPES.POLICY_STICKER,
    UPLOADABLE_DOCUMENT_TYPES.ROADWORTHY,
    UPLOADABLE_DOCUMENT_TYPES.RENEWAL_NOTICE,
    UPLOADABLE_DOCUMENT_TYPES.ID_CARD_BACK
  ]
  const documentListFormatted = documentList.map((doc) => removeUnderscoresAndCapitalize(doc))
  const getPolicyDocuments = (): IDocument[] => {
    if (!policy) return [];
    return [...(policy?.account?.documents || []), ...(policy?.applicant?.customerDocuments || []), ...(policy?.documents || [])]
  }
  
  const listOfAllDocuments = getPolicyDocuments();
  const canApprovePayment = (payment: IPayment) => {
    return payment?.paymentStatus == PAYMENT_STATUS_STATES.APPROVAL && (user.isInternalOwner() || (user?.isBroker() && user.isManager() || user?.isSupervisor() || user?.isOwner()))
  }

  useEffect(() => {
    if (policy && policy?.vehicles && policy?.vehicles.length) {
      setselectedVehicleNumber(String(policy?.vehicles[0].registrationNumber))
    }

  }, [policy, policy?.vehicles])

  const {
    PaymentModalComponent,
    choosePaymentOption,
    InitiateHubtelDirectCharge,
    showHubtelPreapprovalModal,
    sendPaymentNotification,
    showGeneratePaymentObjectsModal,
    showPaymentApprovalModal,
    handleResetPayment,
    generatePaymentLink,
  } = usePayment({
    paystackConfig: paystackConfig,
    policy: policy
  });

  const baseUrl = window.location.origin;

  const fetchPolicy = async () => {
    try {
      if (policyId) {
        const response = await getPolicy(policyId);
        const fetchedPolicy: Policy = response && new Policy(response?.data)
        const vehicles = fetchedPolicy && fetchedPolicy?.vehicles;

        let policyRegistrationNumbers: string[] = [];
        vehicles?.map((vehicle: Vehicle) => {
          policyRegistrationNumbers.push(String(vehicle?.registrationNumber))
        })
        setVehicles(vehicles)
        setPolicy(fetchedPolicy);
        setRegistrationNumbers(policyRegistrationNumbers)
      }
    } catch (error) {
      // showToast.error('Failed to fetch policy details. Please try again later.');
    }
  };

  useEffect(() => {
    dispatch(CLEAR_QUOTE_WIDGET_PAYLOAD())
  }, [])

  const refreshBlockers = React.useCallback(async () => {
    if (!policy?.ref || policy?.active) {
      setBlockers([]);
      return;
    }
    try {
      const list = await fetchPolicyBlockers(policy.ref);
      setBlockers(Array.isArray(list) ? list : []);
    } catch {
      setBlockers([]);
    }
  }, [policy?.ref, policy?.active]);

  useEffect(() => {
    refreshBlockers();
  }, [refreshBlockers]);

  // When the policy is in IN_REVIEW (or stages that funnel through pre-approval) AND blockers exist,
  // surface "Pending Requirements" instead of the raw stage so brokers see why it's stuck.
  const hasBlockers = blockers.length > 0;
  const stagesEligibleForPendingLabel = [
    POLICY_REVIEW_STATES.IN_REVIEW,
    POLICY_REVIEW_STATES.IN_APPROVAL,
    POLICY_REVIEW_STATES.NEW,
    POLICY_REVIEW_STATES.ON_HOLD,
  ];
  const showPendingRequirementsLabel = hasBlockers
    && policy?.reviewStage
    && stagesEligibleForPendingLabel.includes(policy.reviewStage as any);
  const reviewStageDisplay = showPendingRequirementsLabel
    ? 'Pending Requirements'
    : capitalizeSentence(policy?.reviewStage ?? '-');

  const form = useMemo(() => <AddVehicleForm policyId={String(policyId)} closeModal={closeModal} />, [policyId]);
  const addNewVehicleModal = () => {
    updateModalStates({
      show: true,
      static: true,
      title: `Add New Vehicle To Policy`,
      size: '4xl',
      children: form,
      classes: '!p-0 r-[5px]',
    });
  };

  const fetchSinglePayout = async () => {
    try {
      if (policyId) {
        const response = await getPayoutByPolicyNumber(policyId);

        setPayout(response?.data);
      }
    } catch (error: any) {
      // showToast.error('Failed to fetch payout. Please try again later.');
    }

  }

  const fetchPolicyClaims = async () => {
    try {
      if (policyId) {
        const response: any = await getPolicyClaims(policyId);
        setClaims(response);
      }
    } catch (error) {
      // showToast.error('Failed to fetch policy details. Please try again later.');
    }
  };

  const fetchCommissions = async () => {
    policyId && await getCommissionsByApplicationNumber(policyId)
      .then((response: any) => {
        setCommission(new Commission(response));
        setSettlement(new Settlement(response?.payout))
      })
      .catch((error: any) => {
        // showToast.error(error.description);
      })
      .finally(() => { })
  }

  const resolveFinancial = () => {
    if (!(settlement && payout)) {
          updateModalStates({
            show: true,
            size: 'lg',
            children: (
              <ActionConfirmation
                action="Proceed"
                type='warning'
                confirmationMessage='You are about to record financials for this policy. Confirm to proceed.'
                onClose={closeModal}
                onConfirm={async () => {
                  await resolveFinancials(String(policyId))
                  .then(() => { 
                    showToast.success('Financials recorded successfully');
                    closeModal();
                    invalidatePolicy();
                    handleInvalidatePayoutsQueries();
                    handleInvalidateCommissionsQueries();
                  })
                  .catch((error: any) => {
                    showToast.error(error.description || 'Failed to record financials. Please try again later.');
                    closeModal();
                  })
                }}
              />
            ),
            showCloseButton: true,
            title: 'Record Financials',
            classes: '!p-0 r-[5px]',
        });
      }

  }

  useQuery({
    queryKey: ['fetchCommissions'],
    queryFn: fetchCommissions,
  })

  const { isLoading } = useQuery({
    queryKey: ['fetchPolicy', params, refresh],
    queryFn: fetchPolicy,
    enabled: !!policyId
  });

  const { } = useQuery({
    queryKey: ['fetchSinglePayout', params, refresh],
    queryFn: fetchSinglePayout,
    enabled: !!policyId
  });

  const invalidateQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['fetchPolicy'] });
  };

  const { isLoading: claimsLoading } = useQuery({
    queryKey: ['fetchPolicyClaims', activeTab == 'claims'],
    queryFn: fetchPolicyClaims,
    enabled: !!policyId
  });

  const makePaymentForSelectedIds = async (selectedPayments: any, policy: Policy) => {
    try {
      if (selectedPayments) {
        const payload = {
          ...selectedPayments,
          paymentIds: selectedPayments
        };
        const response = await makeSelectedPaymentsForAPolicy(payload);
        const paymentLink = response?.data?.data;
        policy && policy?.applicant && showMotorPaymentLinkModal(paymentLink, policy?.applicant?.phoneNumber, undefined);
      }
    } catch (error: any) {
      showToast.error(error.description ?? 'Failed to generate payment link. Please try again later.');
    }
  };

  // Row menu — non-initiation only. All payment-initiation entries (Get Link,
  // Process Offline Payment, Initiate Direct Charge, etc.) live in the top
  // "Payment Action" dropdown; row menu keeps actions that are inherently
  // row-scoped (Approve this payment, View its evidence, Reset it).
  const getPaymentRowActions = (payment: IPayment): ITableAction[] => {
    const actions: ITableAction[] = [];

    if (payment && canApprovePayment(payment)) {
      actions.push({
        label: 'Approve',
        icon: <CheckCircle className='h-4 w-4' />,
        callback: () => {
          if (payment) showPaymentApprovalModal(payment, invalidateQueries);
        }
      });
    }

    if ((user.isInternalSupervisor() || user.isInternalOwner()) && payment?.evidenceUrl) {
      actions.push({
        label: `${payment?.paymentStatus === PAYMENT_STATUS_STATES.SUCCESS ? 'View Receipt' : 'View evidence'}`,
        icon: <Eye className="h-4 w-4" />,
        callback: () => {
          if (payment) openPictureModal(payment?.evidenceUrl);
        }
      });
    }

    if (
      payment &&
      payment.paymentStatus !== PAYMENT_STATUS_STATES.SUCCESS &&
      payment.paymentStatus !== PAYMENT_STATUS_STATES.VOID &&
      user?.isInternalAdmin()
    ) {
      actions.push({
        label: 'Reset payment',
        icon: <RotateCw className='h-4 w-4' />,
        callback: () => {
          if (payment) handleResetPayment(payment, invalidateQueries);
        }
      });
    }

    return actions;
  };

  const showRefereePhoneVerificationModal = () => {
    updateModalStates({
      show: true,
      size: 'md',
      children: (
        <RefereePhoneVerificationModal
          policy={policy}
          close={closeModal}
          onProceed={() => {
            closeModal();
            queryClient.invalidateQueries({ queryKey: ['fetchPolicy'] });
          }}
        />
      ),
      title: 'Verify Referee Phone Number',
      classes: '!p-0 r-[5px]'
    });
  };

  const scrollToPaymentHistory = () => {
    if (pageRef.current) {
      pageRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }

  const totalPaymentsAmount = () => {
    const amount = policy?.payments && policy?.payments?.map((payment: any) => {
      return payment?.paymentStatus == PAYMENT_STATUS_STATES.SUCCESS && payment?.amount;
    });

    let startVal = 0;
    let x = 0;
    const amountLength = amount?.length ? amount?.length : 0;
    if (amount) {
      while (x < amountLength) {
        startVal = startVal + amount[x];
        x++;
      }
    }


    return startVal;
  };

  const balance = () => {
    return policy?.quote?.paymentPlan?.totalPremium ? (policy?.quote?.paymentPlan?.totalPremium - totalPaymentsAmount()).toFixed(2) : 0
  }

  const getTableRowActions = (data: any) => {
    let actions = [
      {
        label: 'Update task',
        callback: () => policyId && showCreateTaskModal(setRefresh, policyId, data, true)
      }
    ];

    return actions;
  };

  const vehicleColumns = useMemo(() => {
    const baseColumns = [
      {
        Header: 'Registration Number',
        accessor: 'registrationNumber',
        Cell: (row: any) => {
          return <span>{row.cell.value ? row.cell.value : '-'}</span>;
        }
      },
      {
        Header: 'Make',
        accessor: 'make',
        Cell: (row: any) => {
          return <span>{row.cell.value && row.cell.value !== 'N/A' ? row.cell.value : '-'}</span>;
        }
      },
      {
        Header: 'Model',
        accessor: 'model',
        Cell: (row: any) => {
          return <span>{row.cell.value && row.cell.value !== 'N/A' ? row.cell.value : '-'}</span>;
        }
      },
      {
        Header: 'Issued Value',
        accessor: 'insuredValue',
        Cell: (row: any) => {
          return <span>{row.cell.value ?? '-'}</span>;
        }
      },
      {
        id: 'Action',
        Cell: (row: any) => {
          return (
            <button
              className="inline-flex items-center px-3 py-2 rounded-md bg-green-600 text-white text-xs font-semibold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => {
                setselectedVehicleNumber(row.row.original.registrationNumber)
              }}
            >
              More info
              <ChevronRight className="h-4 w-4 ml-2" />

            </button>
          );
        }
      }
    ]

    return [
      {
        Header: 'M',
        columns: baseColumns
      }
    ];
  }, []);

  const taskColumns = useMemo(() => {
    const baseColumns = [
      {
        Header: 'Name',
        accessor: 'name',
        Cell: (row: any) => {
          return <span>{row.cell.value ? capitalizeSentence(row.cell.value) : '-'}</span>;
        }
      },
      {
        Header: 'Type',
        accessor: 'type',
        Cell: (row: any) => {
          return <span>{row.cell.value ?? '-'}</span>;
        }
      },
      {
        Header: 'Assigned To',
        accessor: 'assignedTo.firstName',
        Cell: (row: any) => {
          return (
            <span>
              {row.cell.value
                ? capitalizeSentence(
                  row.row.original.assignedTo.firstName +
                  ' ' +
                  row.row.original.assignedTo.lastName
                )
                : '-'}
            </span>
          );
        }
      },
      {
        Header: 'Status',
        accessor: 'completed',
        Cell: (row: any) => {
          return getStatus(row.cell.value);
        }
      },
      {
        Header: 'Due date',
        accessor: 'dueOn',
        Cell: (row: any) => {
          return (
            <span>
              {row.cell.value ? getFormattedDate({ date: row.cell.value, showTime: true }) : '-'}
            </span>
          );
        }
      },
      {
        id: 'Action',
        Cell: (row: any) => {
          return (
            <div className="cursor-pointer flex justify-center">
              {getTableRowActions(row.row.original).length > 0 && (
                <PopOnMe actions={getTableRowActions(row.row.original)}>
                  <EllipsisVerticalIcon className="h-4 w-4" />
                </PopOnMe>
              )}
            </div>
          );
        }
      }
    ]

    return [
      {
        Header: 'M',
        columns: baseColumns
      }
    ];
  }, []);

  const claimsColumns = useMemo(
    () => [
      {
        Header: 'M',
        columns: [
          {
            Header: 'Claim Number',
            accessor: 'claimNumber',
            Cell: (row: any) => {
              return (
                <Link to={`${getDashboardBase()}/claims/${row?.cell.value}`} target="_blank">
                  <span className="hover:underline text-link-main dark:text-darkBg-textLink dark:hover:text-darkBg-textLink">{row.cell.value ?? '-'}</span>
                </Link>
              );
            }
          },

          {
            Header: 'Amount/ Cost',
            accessor: 'amount',
            Cell: (row: any) => {
              return <span>{row.cell.value ?? '-'}</span>;
            }
          },
          {
            Header: 'NCD Effect',
            accessor: 'ncdEffect',
            Cell: (row: any) => {
              return <span>{row.cell.value ?? '-'}</span>;
            }
          },

          {
            Header: 'Stage',
            accessor: 'claimStage',
            Cell: (row: any) => {
              return <ClaimStages status={row.cell.value} />;
            }
          },

          {
            Header: 'Paid',
            accessor: 'paid',
            Cell: (row: any) => {
              return <TwoStateVerifiedCheck verified={row.cell.value} />;
            }
          },
          {
            Header: 'Created On',
            accessor: 'createdOn',
            Cell: (row: any) => {
              return <span>{getFormattedDate({ date: row.cell.value, showTime: true }) ?? '-'}</span>;
            }
          },
          {
            Header: 'Updated On',
            accessor: 'updatedOn',
            Cell: (row: any) => {
              return <span>{getFormattedDate({ date: row.cell.value, showTime: true }) ?? '-'}</span>;
            }
          }
        ]
      }
    ],
    []
  );

  const uploadDoc = () => {
    return policy?.ref && policy?.documents &&
      uploadPolicyDocument(policy?.ref,
        'fetchPolicy',
        documentListFormatted,
        UPLOAD_IMAGE_ENDPOINTS.DOCUMENT,
        'auto',
        listOfAllDocuments);
  }

  const getPolicyActions = () => {
    const policyActions = [];

    if (policy) {
      policyActions.push(
        {
          label: 'Download policy',
          icon: <Download className="h-4 w-4" />,
          callback: () => showDownloadPolicyDetailsModal(policy?.ref)
        },
        {
          label: 'Request vehicle photos',
          icon: <Camera className="h-4 w-4" />,
          callback: () => selectVehicleId && getPolicyVehiclePhotos(policy.ref, Number(selectVehicleId))
        },
        {
          label: 'Share document',
          icon: <Share2 className="h-4 w-4" />,
          callback: () => sendAgreementFormToCustomer(policy, false)
        },

        {
          label: `View / add ${policy?.updates?.length === 1 ? ' update' : ' updates'}`,
          icon: <MessageSquarePlus className="h-4 w-4" />,
          callback: () => setShowUpdates(true)
        },
        {
          label: 'Upload Document',
          icon: <Upload className="h-4 w-4" />,
          callback: () => uploadDoc()
        }
      );

      // if (policy?.insurerPolicyNumber) {
      //   policyActions.push({
      //     label: 'Delete policy',
      //     icon: <Trash className='h-4 w-4' />,
      //     callback: () => handleDeletePolicyByNumber(policy?.ref),
      //   })
      // }

      if (!policy?.dataComplete) {
        policyActions.push(
          {
            label: 'Edit Policy',
            icon: <Edit2 className="h-4 w-4" />,
            callback: () => {
              navigate(`${getDashboardBase()}/policies/${policy?.ref}/edit?type=motor`)
            }
          },
        )
      }

      if (!policy?.renewed) {
        policyActions.push({
          label: 'Mark as renewed',
          icon: <CheckCircle2 className="h-4 w-4" />,
          callback: () => markAsRenewed({ vehicleMake: policy?.vehicles?.[0]?.make, vehicleModel: policy?.vehicles?.[0]?.model, customerName: policy?.applicant?.fullName || '', policyNumber: policy?.ref, policyType: 'VEHICLE', onProceed: invalidatePolicy }),
        })
      }

      if (user?.isInternalAdmin() || user?.isBroker()) {
        policyActions.push(
          {
            label: 'Apply Premium Financing',
            icon: <AppWindow className="h-4 w-4" />,
            callback: () => {
              if (policy) {
                handleApplyLoanToPolicy(policy, invalidatePolicy)
              }
            }
          },
        )
      }

      if (user?.isInternalAdmin()) {
        policyActions.push(
          {
            label: 'Create task',
            icon: <PlusCircle className='h-4 w-4' />,
            callback: () => policyId && showCreateTaskModal(setRefresh, policyId)
          },
          {
            label: 'Request review',
            icon: <ClipboardCheck className="h-4 w-4" />,
            callback: () => sendReviewLinkToCustomer(policy)
          },
          {
            label: 'Regenerate payment schedules',
            icon: <RotateCw className="h-4 w-4" />,
            callback: () =>
              showGeneratePaymentObjectsModal('REGENERATE_PAYMENT_OBJECTS', invalidateQueries)
          },
          {
            label: 'Generate documents',
            icon: <FilePlus className="h-4 w-4" />,
            callback: () => handleGeneratePolicyDocuments(policy)
          },
          {
            label: 'Generate Sticker',
            icon: <BadgeCheck className="h-4 w-4" />,
            callback: () => showGenerateStickerForm(policy, invalidatePolicy)
          },
        )
      }

      if (user?.isInternalAdmin() && policy?.isApproved()) {
        policyActions.push({
          label: 'Generate sale ',
          icon: <ReceiptText className='h-4 w-4' />,
          callback: () => handleGenerateSaleForAPolicy(policy)
        })
      }

      if (policy?.metaData?.vehiclePhotosReceived) {
        policyActions.push({
          label: 'Reload vehicle photos',
          icon: <RotateCw className="h-4 w-4" />,
          callback: () => handleReloadPolicyVehiclePhotos(policy)
        });
      }

      if (!policy.payments || policy.payments.length === 0) {
        policyActions.push({
          label: 'Generate payment schedules',
          icon: <RotateCw className="h-4 w-4" />,
          callback: () =>
            showGeneratePaymentObjectsModal('GENERATE_PAYMENT_OBJECTS', invalidateQueries)
        });
      }

      if (!user.isInternalAdmin() && policy?.active || policy.isExpired()) {
        policyActions.push({
          label: 'Renew policy',
          icon: <Repeat className="h-4 w-4" />,
          callback: () => renewMotorPolicy(policy)
        });
      }

      if (policy?.active && policy?.isApproved()) {
        policyActions.push({
          label: 'Make claim',
          icon: <FileText className="h-4 w-4" />,
          callback: () => policyId && createClaim(policyId, true)
        });
      }

      if (policy?.active) {
        if (user.isInternalOwner() || user.isInternalSupervisor() || user.isInternalOwner()) {
          policyActions.push({
            label: 'Request policy cancellation',
            icon: <FileMinus className="h-4 w-4" />,
            callback: () => handleRequestToCancelPolicy(policy, invalidatePolicy)
          });
        }
      }

      if (!policy.active) {
        if (policy?.metaData?.completed && (user?.isInternalOwner() && !(user?.isOwner() || user?.isSupervisor()))) {
          policyActions.push({
            label: 'Mark as done',
            icon: <CheckCircle className="h-4 w-4" />,
            callback: () => showMarkPolicyAsCompleteModal(policy)
          })
        }
      }


      policyActions.push();

      if ((user.isAdministrator() || user.isSupervisor() || user.isInsurer()) && !policy.isApproved()) {
        policyActions.push({
          label: 'Set to due later',
          icon: <Clock className="h-4 w-4" />,
          callback: () => setPolicyToDueLater(policy)
        });
      }

      if (!(policy?.reviewStage === 'ARCHIVED' || policy?.reviewStage === 'APPROVED' || policy?.active)) {
        policyActions.push({
          label: 'Archive',
          icon: <Archive className='h-4 w-4' />,
          callback: () => archiveAPolicy(policy, true),

        });
      }

    }

    return sortArrayByAlphabets(policyActions);
  };

  const getButtonActions = () => {
    const policyActions = [
    ];

    if (policy?.active) {
      if (user.isOwner()) {
        policyActions.push({
          label: 'Cancel policy',
          icon: <XCircle className="h-4 w-4" />,
          callback: () => handleCancelPolicy(policy, invalidatePolicy)
        });
      }
    }

    if (!policy?.active) {
      if (policy?.metaData?.firstPaymentReceived && (user?.isAdministrator() || user.isBroker() || user.isInsurer())) {
        policyActions.push({
          label: 'Submit for review',
          icon: <FileSearch className="h-4 w-4" />,
          callback: () => handleSubmitForReview(policy, invalidatePolicy)
        });
      }

      if (user.isOwner()) {
        if (!policy?.isOnHold() && policy?.isInApproval()) {
          policyActions.push({
            label: 'On Hold',
            icon: <Pause className="h-4 w-4" />,
            callback: () => handleSetPolicyOnHold(policy, invalidatePolicy)
          });
        }

        if (!policy?.isDeclined() && policy?.isInApproval()) {
          policyActions.push({
            label: 'Decline',
            icon: <FileSearch className="h-4 w-4" />,
            callback: () => handleDeclinePolicy(policy, invalidatePolicy)
          });
        }
      }

      if (user.isSupervisor() || user.isAgent()) {
        if (!policy?.isOnHold() && policy?.isInReview()) {
          policyActions.push({
            label: 'On Hold',
            icon: <Pause className="h-4 w-4" />,
            callback: () => handleSetPolicyOnHold(policy, invalidatePolicy)
          });
        }


        if (policy?.isOnHold() || policy?.isInReview() || policy?.isDueLater()) {
          policyActions.push({
            label: 'Submit for approval',
            icon: <Send className="h-4 w-4" />,
            callback: () => handleSubmitForApproval(policy, invalidatePolicy)
          });
        }
      }

      if ((user.isInternalOwner() || user.isInternalSupervisor()) && policy?.isDueLater()) {
        policyActions.push({
          label: 'On Hold',
          icon: <Pause className="h-4 w-4" />,
          callback: () => handleSetPolicyOnHold(policy, invalidatePolicy)
        });
      }
    }

    if ((policy?.metaData?.firstPaymentReceived && policy?.isInApproval() && (user.isInternalAdmin() || user?.isBroker()) && !user.isAgent())) {
      policyActions.push({
        label: 'Approve',
        icon: <CheckCircle className="h-4 w-4" />,
        callback: () => handleApprovePolicy(policy, () => {
          queryClient.invalidateQueries({ queryKey: ['fetchPolicy'] });
        })
      });
    }

    return sortArrayByAlphabets(policyActions);
  };

  const getLatestInApprovalUpdate = () => {
    const inApprovalUpdates = policy?.updates?.filter(
      (update: IPolicyUpdate) => update.reviewStage === POLICY_REVIEW_STATES.IN_APPROVAL
    );

    if (inApprovalUpdates) {
      return inApprovalUpdates[inApprovalUpdates.length - 1];
    }
  };

  const columns = React.useMemo(
    () => [
      {
        id: 'selection',
        Header: ({ getToggleAllRowsSelectedProps }: any) => (
          <div className="">
            <input type="checkbox" {...getToggleAllRowsSelectedProps()} />
          </div>
        ),
        Cell: ({ row }: any) => (
          <div className="">
            <input type="checkbox" {...row.getToggleRowSelectedProps()} />
          </div>
        )
      },
      {
        Header: 'M',
        columns: [
          {
            Header: 'ID',
            accessor: 'id',
            Cell: (row: any) => {
              return row.cell.value ? <span className="">{row.cell.value}</span> : <ShowPendingInfo/>;
            }
          },
          {
            Header: 'Due Date',
            accessor: 'dueDate',
            Cell: (row: any) => {
              return (
                row.cell.value ? <span>{getFormattedDate({ date: row.cell.value, showTime: false })}</span> : <ShowPendingInfo/>
              );
            }
          },
          {
            Header: 'Paid Date',
            accessor: 'dateOfPayment',
            Cell: (row: any) => {
              return (
                row.cell.value ? <span>{getFormattedDate({ date: row.cell.value, showTime: false })}</span> : <ShowPendingInfo/>
              );
            }
          },
          {
            Header: 'Channel',
            accessor: 'channel',
            Cell: (row: any) => {
              return (
                row.cell.value ? <span className="">{getFormattedPaymentChannel(row.cell.value)}</span> : <ShowPendingInfo/>
              );
            }
          },
          {
            Header: 'Processor',
            accessor: 'processor',
            Cell: (row: any) => {
              return row.cell.value ? <span className="">{row.cell.value}</span> : <ShowPendingInfo/>;
            }
          },
          {
            Header: 'Amount',
            accessor: 'preAmount',
            Cell: (row: any) => {
              return row.cell.value ? <span className="">{row.cell.value}</span> : <ShowPendingInfo/>;
            }
          },
          {
            Header: 'Charged Amount',
            accessor: 'amount',
            Cell: (row: any) => {
              return row.cell.value ? <span className="">{row.cell.value}</span> : <ShowPendingInfo/>;
            }
          },
          {
            Header: 'Processor Ref',
            accessor: 'reference',
            Cell: (row: any) => {
              return row.cell.value ? <span className="">{row.cell.value}</span> : <ShowPendingInfo/>;
            }
          },
          {
            Header: 'Status',
            accessor: 'paymentStatus',
            Cell: (row: any) => {
              return <PaymentStatus row={row} actions={getPaymentRowActions(row.row.original)} />;
            }
          }
        ]
      }
    ],
    []
  );

  const mobileColumns = React.useMemo(
    () => [
      {
        id: 'selection',
        Header: ({ getToggleAllRowsSelectedProps }: any) => (
          <div className="">
            <input type="checkbox" {...getToggleAllRowsSelectedProps()} />
          </div>
        ),
        Cell: ({ row }: any) => (
          <div className="">
            <input type="checkbox" {...row.getToggleRowSelectedProps()} />
          </div>
        )
      },
      {
        Header: 'M',
        columns: [
          {
            Header: 'ID',
            accessor: 'id',
            Cell: (row: any) => {
              return row.cell.value ? <span className="">{row.cell.value}</span> : <ShowPendingInfo/>;
            }
          },
          {
            Header: 'Due Date',
            accessor: 'dueDate',
            Cell: (row: any) => {
              return (
                row.cell.value ? <span>{getFormattedDate({ date: row.cell.value, showTime: false })}</span> : <ShowPendingInfo/>
              );
            }
          },
          {
            Header: 'Paid Date',
            accessor: 'dateOfPayment',
            Cell: (row: any) => {
              return (
                row.cell.value ? <span>{getFormattedDate({ date: row.cell.value, showTime: false })}</span> : <ShowPendingInfo/>
              );
            }
          },
          {
            Header: 'Channel',
            accessor: 'channel',
            Cell: (row: any) => {
              return row.cell.value ? <span className="">{row.cell.value}</span> : <ShowPendingInfo/>;
            }
          },
          {
            Header: 'Scheduled Amount',
            accessor: 'preAmount',
            Cell: (row: any) => {
              return row.cell.value ? <span className="">{row.cell.value}</span> : <ShowPendingInfo/>;
            }
          },
          {
            Header: 'Penalty',
            accessor: 'incrementAmount',
            Cell: (row: any) => {
              return <span className="">{row.cell.value ?? '-'}</span>;
            }
          },
          {
            Header: 'Total',
            accessor: 'amount',
            Cell: (row: any) => {
              return row.cell.value ? <span className="">{row.cell.value}</span> : <ShowPendingInfo/>;
            }
          },
          {
            Header: 'Status',
            accessor: 'paymentStatus',
            Cell: (row: any) => {
              return <PaymentStatus row={row} />;
            }
          },
          {
            id: 'Action',
            Header: 'Actions',
            Cell: (row: any) => {
              if (getPaymentRowActions(row.row.original)?.length > 0)
                return (
                  <div className="cursor-pointer flex justify-center">
                    <PopOnMe actions={getPaymentRowActions(row.row.original)}>
                      <EllipsisVerticalIcon className="h-4 w-4" />
                    </PopOnMe>
                  </div>
                );
            }
          }
        ]
      }
    ],
    []
  );

  // Single consolidated "Payment Action" dropdown — all payment initiation lives here.
  // Per-payment entries appear when exactly one row is selected; bulk entries when
  // multiple are; policy-level entries are always present (subject to role/state gates).
  // Order is intentional (per-payment → bulk → policy-level) — do NOT alpha-sort.
  const getPaymentActions = () => {
    if (!policy) return [];

    const allPaymentsNotSuccessfull = (policy.payments || [])
      .filter((p) => p.paymentStatus !== PAYMENT_STATUS_STATES.SUCCESS);

    const selectionCount = selectedPayments?.length ?? 0;
    // `selectedPayments` is an array of payment ids (see selectionChanged on the GTable).
    // policy.payments items are typed ICustomerPayments — the rest of this file
    // (e.g. getPaymentRowActions) treats them as IPayment, so we mirror that cast.
    const oneSelected: IPayment | null =
      selectionCount === 1
        ? ((policy.payments?.find((p: any) => p.id === selectedPayments[0]) as IPayment | undefined) ?? null)
        : null;
    const oneSelectedIsInitiable = !!oneSelected
      && oneSelected.paymentStatus !== PAYMENT_STATUS_STATES.SUCCESS
      && oneSelected.paymentStatus !== PAYMENT_STATUS_STATES.VOID;

    const actions: ITableAction[] = [];

    // --- Per-payment (require exactly one initiable row selected) ---
    if (oneSelectedIsInitiable && oneSelected) {
      actions.push(
        {
          label: 'Get online payment link',
          icon: <Paperclip className='h-4 w-4' />,
          callback: () => {
            generatePaymentLink(oneSelected.id, (response) => {
              const paymentLink = response?.data?.data;
              const paymentId = Number(oneSelected.id);
              const phoneNumber = policy?.applicant ? policy.applicant.phoneNumber : undefined;
              paymentLink && showMotorPaymentLinkModal(paymentLink, phoneNumber, paymentId);
            });
          }
        },
        {
          label: 'Process offline payment',
          icon: <Edit className='h-4 w-4' />,
          callback: () => showEditPaymentModal(oneSelected, policyId)
        },
        {
          label: 'Initiate direct charge',
          icon: <CreditCard className='h-4 w-4' />,
          callback: () => InitiateHubtelDirectCharge(oneSelected)
        },
      );
    }

    // --- Bulk ---
    if (selectionCount > 1) {
      actions.push({
        label: `Pay selected (${selectionCount})`,
        icon: <CheckCircle className='h-4 w-4' />,
        callback: () => makePaymentForSelectedIds(selectedPayments, policy)
      });
    }
    if (allPaymentsNotSuccessfull.length > 0) {
      actions.push({
        label: 'Complete all payments',
        icon: <FileCheck className='h-4 w-4' />,
        callback: () => { if (policyId) completeAllPayments(policyId, policy, setLoading); }
      });
    }

    // --- Policy-level ---
    actions.push(
      {
        label: 'Activate preapproval',
        icon: <CheckCircle className='h-4 w-4' />,
        callback: () => showHubtelPreapprovalModal()
      },
      {
        label: 'Generate payment receipt',
        icon: <FileCheck className='h-4 w-4' />,
        callback: () => handleGeneratePaymentReceipt(policy)
      },
    );

    if (policy.isFullPayment() && policy.getFirstUnpaidPayment()) {
      actions.push({
        label: 'Make payment',
        icon: <CheckCircle className='h-4 w-4' />,
        callback: () => choosePaymentOption()
      });
    }

    if (!policy.payments || policy.payments.length === 0) {
      actions.push({
        label: 'Generate payment schedules',
        icon: <RotateCw className='h-4 w-4' />,
        callback: () =>
          showGeneratePaymentObjectsModal('GENERATE_PAYMENT_OBJECTS', invalidateQueries)
      });
    }
    if (user.isInternalAdmin()) {
      actions.push({
        label: 'Regenerate payment schedules',
        icon: <RotateCw className='h-4 w-4' />,
        callback: () =>
          showGeneratePaymentObjectsModal('REGENERATE_PAYMENT_OBJECTS', invalidateQueries)
      });
    }
    if (!user.isBroker()) {
      actions.push({
        label: 'Apply discount',
        icon: <Percent className='h-4 w-4' />,
        callback: () => applyDiscount(policy)
      });
    }

    return actions;
  };

  const onRefreshAnalysis = () => {
    queryClient.invalidateQueries({ queryKey: ['getVerificationDetails'] })
  }

  const getTabs = () => {
    let tabs: ITab[] = [
      {
        id: 'payments',
        title: 'Payments',
        itemCount: policy?.payments?.length ?? 0,
        icon: <CreditCard />,
        actions: (
          activeTab === "payments" && policy?.payments && policy?.payments?.length > 0 ? (
            <ActionsPopOnMe text="Payment Action" actions={getPaymentActions()} popOptionClasses='' />
          ) : <span></span>
        )
      },
      {
        id: 'claims',
        title: 'Claims',
        itemCount: claims?.length ?? 0,
        icon: <User />
      },
    ];

    return tabs;
  };

  const getOtherInformation = (): JSX.Element => {
    return (
      <div className="bg-white rounded-2xl shadow overflow-hidden mb-6 dark:!text-darkBg-mainText dark:bg-darkBg dark:border dark:border-darkBg-border">
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b dark:border-b-darkBg-border border-gray-200 bg-gray-50 dark:bg-darkBg">
          <h2 className="text-lg font-medium text-gray-900 dark:text-darkBg-mainText flex items-center">
            {/* <Info className="h-6 w-6 mr-2 text-gray-500 dark:text-darkBg-mainText flex items-center" /> */}
            Other Information
            <Tooltip message="Endorse Other Information" placement="bottom">
              <div>
                <Edit onClick={() => policy && handleEditVehicleInformationModal(policy, 'Endorse Other Information', false, false, true, false, false, selectedVehicleNumber)} className="h-4 w-4 ml-2 text-primary-main cursor-pointer" />
              </div>
            </Tooltip>
          </h2>
        </div>
        <div className="p-4 sm:p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <PolicyDetailsItem
                name="Has vehicle been altered in any way?"
                value={policy?.vehicle?.vehicleAltered ? 'Yes' : 'No'}
              />

              {policy?.vehicle?.vehicleAltered && (
                <PolicyDetailsItem
                  name="Vehicle Alteration Details"
                  value={policy?.vehicle?.alterationDetails ?? ''}
                />
              )}

              <PolicyDetailsItem
                name="Is this vehicle in good repair state?"
                value={policy?.vehicle?.goodRepairState ? 'Yes' : 'No'}
              />

              {!policy?.vehicle?.goodRepairState && (
                <PolicyDetailsItem
                  name="Vehicle repair state details"
                  value={policy?.vehicle?.repairState ?? ''}
                />
              )}

              <PolicyDetailsItem
                name="Does driver have any disease or complications?"
                value={policy?.vehicle?.driver?.hasDisease ? 'Yes' : 'No'}
              />

              {policy?.vehicle?.driver?.hasDisease && (
                <PolicyDetailsItem
                  name="Driver disease or complications"
                  value={policy?.vehicle?.driver?.diseaseOrComplications ?? ''}
                />
              )}
            </div>

            <div>
              <PolicyDetailsItem
                name="Ever been declined by other insurer?"
                value={policy?.previouslyDeclined ? 'Yes' : 'No'}
              />

              {policy?.previouslyDeclined && (
                <PolicyDetailsItem
                  name="Reasons for previously declined insurance"
                  value={policy?.declinedInsuranceDetails ?? ''}
                />
              )}

              <PolicyDetailsItem
                name="Have you ever made a claim on another insurance policy?"
                value={policy?.previouslyIssuedClaim ? 'Yes' : 'No'}
              />

              {policy?.previouslyIssuedClaim && (
                <PolicyDetailsItem
                  name="Details for previously issued claim"
                  value={policy?.previousInsuranceClaims ?? ''}
                />
              )}

              <PolicyDetailsItem
                name="Is this vehicle on hire purchase?"
                value={policy?.vehicle?.interestedParties ? 'Yes' : 'No'}
              />

              {policy?.isYango() && (
                <>
                  <PolicyDetailsItem
                    name="Which park does driver belong?"
                    value={policy?.broker?.name}
                  />

                  <PolicyDetailsItem
                    name="Is driver connected to a park?"
                    value={policy?.quote?.connectedDriver ? 'Yes' : 'No'}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const getProviderDetails = (): JSX.Element => {
    return (
      <div className="bg-white rounded-2xl shadow overflow-hidden mb-6 dark:!text-darkBg-mainText dark:bg-darkBg dark:border dark:border-darkBg-border">
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b dark:border-b-darkBg-border border-gray-200 bg-gray-50 dark:bg-darkBg">
          <h2 className="text-lg font-medium text-gray-900 dark:text-darkBg-mainText flex items-center">
            {/* <Info className="h-6 w-6 mr-2 text-gray-500 dark:text-darkBg-mainText flex items-center" /> */}
            Provider Details
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 p-4 sm:p-6 space-y-2">
          <div className="space-y-3">
            <PolicyDetailsItem name="Insurance Provider" value={policy?.insurer?.name ?? <ShowPendingInfo/>} />
            <PolicyDetailsItem name="MID Reference" value={policy?.midReference ?? <ShowPendingInfo/>} />
            <PolicyDetailsItem name="Insurer Reference" value={policy?.insurerPolicyNumber ?? <ShowPendingInfo/>} />
          </div>

          <div className="space-y-3">
            <PolicyDetailsItem name={
              <div className='flex gap-2'>
                <span>Broker</span>
                {user.isInternalAdmin() && <div className='hidden md:block'>
                  <Tooltip message="Assign policy to broker" placement="bottom">
                    <div>
                      <MdChangeCircle onClick={() => policy && assignPolicyToBroker(policy)} className="size-5 bg-primary-main text-white flex items-center justify-center rounded-full p-1 cursor-pointer" />
                    </div>
                  </Tooltip>
                </div>
                }
              </div>}
              value={
                <>
                  <span>{policy?.broker && policy?.broker?.name ? policy?.broker?.name : <ShowPendingInfo/>}</span>

                </>
              } />
            <PolicyDetailsItem name="Premium" value={formatCurrencyAmount(policy?.quote?.paymentPlan?.outRightPremium, undefined, false) ?? <ShowPendingInfo/>} />
          </div>
        </div>
      </div>
    );
  };

  const getPolicyTermsInformation = (): JSX.Element => {
    return (
      <div className="bg-white rounded-2xl shadow overflow-hidden mb-6 dark:!text-darkBg-mainText dark:bg-darkBg dark:border dark:border-darkBg-border">
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b dark:border-b-darkBg-border border-gray-200 bg-gray-50 dark:bg-darkBg">
          <h2 className="text-lg font-medium text-gray-900 dark:text-darkBg-mainText flex items-center">
            {/* <Info className="h-6 w-6 mr-2 text-gray-500 dark:text-darkBg-mainText flex items-center" /> */}
            Policy Terms
          </h2>
        </div>

        <div className="p-4 sm:p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <PolicyDetailsItem name="Insurer Portfolio" value={policy?.motorQuote?.insurerPortfolio ? policy?.motorQuote?.insurerPortfolio?.name : <ShowPendingInfo/>} />
            <PolicyDetailsItem name="Cover Type" value={policy?.protectionType ? getVehicleProtectionFromKey(policy?.protectionType)?.title : <ShowPendingInfo/>} />
            <PolicyDetailsItem name="Cover Period" value={policy?.quote?.coverPeriod ?? <ShowPendingInfo/>} />

          </div>
          <div className="space-y-3">
            <PolicyDetailsItem name="Sum Insured" value={policy?.quote?.insuredValue ?? <ShowPendingInfo/>} />
            <PolicyDetailsItem name="Inception Date" value={policy?.startsOn ? getFormattedDate({ date: policy?.startsOn }) : <ShowPendingInfo/>} />
            <PolicyDetailsItem name="Expiry Date" value={policy?.expiresOn ? getFormattedDate({ date: policy?.expiresOn }) : <ShowPendingInfo/>} />
          </div>
        </div>
      </div>
    );
  };

  const getVehicleSpecificationInfo = (vehicle: any): JSX.Element => {
    return (
      <div className="bg-white rounded-2xl shadow overflow-hidden mb-6 dark:!text-darkBg-mainText dark:bg-darkBg dark:border dark:border-darkBg-border">
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b dark:border-b-darkBg-border border-gray-200 bg-gray-50 dark:bg-darkBg">
          <h2 className="text-lg font-medium text-gray-900 dark:text-darkBg-mainText flex items-center">
            {/* <Info className="h-6 w-6 mr-2 text-gray-500 dark:text-darkBg-mainText flex items-center" /> */}
            Specifications
          </h2>
        </div>

        <div className="p-4 sm:p-6 space-y-3">
          <PolicyDetailsItem name="Make" value={vehicle.make && vehicle.make !== 'UNKNOWN' ? vehicle.make : <ShowPendingInfo/>} />
          <PolicyDetailsItem name="Model" value={vehicle.model && vehicle.model !== 'UNKNOWN' ? vehicle.model : <ShowPendingInfo/>} />
          <PolicyDetailsItem name="Type" value={vehicle.type?.name ?? <ShowPendingInfo/>} />
          <PolicyDetailsItem name="Manufactured Year" value={vehicle.manufacturedYear ?? <ShowPendingInfo/>} />
          {vehicle.chassisNumber ? <PolicyDetailsItem name="Chassis / VIN Number" value={vehicle.chassisNumber && vehicle.chassisNumber !== 'N/A' ? vehicle.chassisNumber : <ShowPendingInfo/>} append={vehicle.chassisNumber &&
            <>
              {policy?.vehicle?.verified ? (
                <IconStatus
                  cursorPointer
                  status={true}
                />
              ) : (
                <ToolTip message={`Click to verify`} placement='bottom'>
                  <div>
                    <IconStatus
                      cursorPointer
                      onclick={() => policy && manuallyVerifyVIN(policy)}
                      status={policy?.vehicle?.verified}
                    />
                  </div>
                </ToolTip>
              )}
            </>
          } />
            : <ShowPendingInfo/>}
          <PolicyDetailsItem name="CC" value={vehicle.cc ?? <ShowPendingInfo/>} />
        </div>
      </div>
    );
  };

  const getOwnerAndValueInfo = (vehicle: any): JSX.Element => {
    return (
      <div className="bg-white rounded-2xl shadow overflow-hidden mb-6 dark:!text-darkBg-mainText dark:bg-darkBg dark:border dark:border-darkBg-border">
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b dark:border-b-darkBg-border border-gray-200 bg-gray-50 dark:bg-darkBg">
          <h2 className="text-lg font-medium text-gray-900 dark:text-darkBg-mainText flex items-center">
            {/* <Info className="h-6 w-6 mr-2 text-gray-500 dark:text-darkBg-mainText flex items-center" /> */}
            Owner & Usage
            {policy?.active &&
              <Tooltip message="Endorse Vehicle Owner Information" placement="bottom">
                <div>
                  <Edit onClick={() => policy && handleEditVehicleInformationModal(policy, 'Edit Vehicle Owner Information', false, true, false, false, false, selectedVehicleNumber)} className="h-4 w-4 ml-2 text-primary-main cursor-pointer" />
                </div>
              </Tooltip>
            }
          </h2>
        </div>
        <div className=" grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 p-4 sm:p-6">

          <div className="space-y-3">
            <PolicyDetailsItem name="Vehicle Owner" value={policy?.applicant?.fullName ?? <ShowPendingInfo/>} />
            <PolicyDetailsItem name="Owner's Phone" value={policy?.applicant?.phoneNumber ? getPhoneNumberFromShortCodeFormat(policy.applicant?.phoneNumber) : <ShowPendingInfo/>} />
            <PolicyDetailsItem name="Vehicle Main Driver" value={vehicle.driver?.fullName ?? <ShowPendingInfo/>} />
          </div>
          <div className="space-y-3">
            <PolicyDetailsItem name="Nature of Interest in Vehicle" value={vehicle?.natureOfInterest ? capitalizeSentence(vehicle.natureOfInterest) : <ShowPendingInfo/>} />
            <PolicyDetailsItem name="Vehicle Insurance Value" value={(vehicle?.insuredValue ?? 0)} />
          </div>
          <div className="space-y-3">
            <PolicyDetailsItem name="Accessories Installed" value={vehicle.installedAccessoriesDetails ?? <ShowPendingInfo/>} />
            <PolicyDetailsItem name="Accessories Value" value={(vehicle.installedAccessoriesValue ?? 0)} />
          </div>
        </div>
      </div>
    );
  };

  const getVehicleRegistrationAndUsageInfo = (vehicle: any): JSX.Element => {
    return (
      <div className="bg-white rounded-2xl shadow overflow-hidden mb-6 dark:!text-darkBg-mainText dark:bg-darkBg dark:border dark:border-darkBg-border">
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b dark:border-b-darkBg-border border-gray-200 bg-gray-50 dark:bg-darkBg">
          <h2 className="text-lg font-medium text-gray-900 dark:text-darkBg-mainText flex items-center">
            {/* <Info className="h-6 w-6 mr-2 text-gray-500 dark:text-darkBg-mainText flex items-center" /> */}
            Registration & Usage
          </h2>
        </div>

        <div className="p-4 sm:p-6 space-y-3">
          <PolicyDetailsItem name="Color" value={vehicle.color && vehicle.color !== 'N/A' ? vehicle.color : <ShowPendingInfo/>} />
          <PolicyDetailsItem name="Registration Year" value={vehicle.registrationYear ?? <ShowPendingInfo/>} />
          <PolicyDetailsItem name="Vehicle Registration Number" value={vehicle.registrationNumber ?? <ShowPendingInfo/>} />
          <PolicyDetailsItem name="Vehicle Usage" value={vehicle.usage?.title ?? <ShowPendingInfo/>} />
          <PolicyDetailsItem name="Number of Passengers" value={vehicle.maxSeat ?? <ShowPendingInfo/>} />
          <PolicyDetailsItem name="Vehicle Region" value={vehicle.region ?? <ShowPendingInfo/>} />
        </div>
      </div>
    );
  };

  const getRefereeDetails = (): JSX.Element => {
    return (
      <div className="bg-white rounded-2xl shadow overflow-hidden mb-6 dark:!text-darkBg-mainText dark:bg-darkBg dark:border dark:border-darkBg-border">
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b dark:border-b-darkBg-border border-gray-200 bg-gray-50 dark:bg-darkBg">
          <h2 className="text-lg font-medium text-gray-900 dark:text-darkBg-mainText flex items-center">
            <PersonStanding className="h-6 w-6 mr-2 text-gray-500 dark:text-darkBg-mainText flex items-center" />
            Referee Information
            <Tooltip message="Endorse Referee Information" placement="bottom">
              <div>
                <Edit onClick={() => policy && handleEditVehicleInformationModal(policy, 'Endorse Referee Information', false, false, false, false, true, selectedVehicleNumber)} className="h-4 w-4 ml-2 text-primary-main cursor-pointer" />
              </div>
            </Tooltip>
          </h2>
        </div>
        <div className="p-4 sm:p-6">
          <div className="grid grid-cols-1 gap-6">
            <PolicyDetailsItem name="Name" value={policy?.referee?.name ?? <ShowPendingInfo/>} />
            <PolicyDetailsItem name="Relationship" value={policy?.referee?.relationship ?? <ShowPendingInfo/>} />
            <PolicyDetailsItem name="Years known" value={policy?.referee?.yearsKnown ?? <ShowPendingInfo/>} />
            <PolicyDetailsItem
              name="Phone"
              value={policy?.referee?.phone ? getPhoneNumberFromShortCodeFormat(policy?.referee?.phone) : <ShowPendingInfo/>}
              prepend={
                policy?.referee?.phone && (
                  <>
                    {policy?.referee?.phone && policy?.referee?.verified ? (
                      <div className="">
                        <CheckIcon className=" h-4 w-4 rounded-full" />
                      </div>
                    ) : (
                      <ToolTip message={`Click to verify`}>
                        <div className="" onClick={showRefereePhoneVerificationModal}>
                          <XMarkIcon className="text-white p-[2px] h-4 w-4 rounded-full bg-red-700 cursor-pointer" />
                        </div>
                      </ToolTip>
                    )}
                  </>
                )}
            />
          </div>
        </div>
      </div>
    );
  };

  const getCommunityDetails = (): JSX.Element => {
    return (
      <div className="bg-white rounded-2xl shadow overflow-hidden dark:!text-darkBg-mainText dark:bg-darkBg dark:border dark:border-darkBg-border mb-6">
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b dark:border-b-darkBg-border border-gray-200 bg-gray-50 dark:bg-darkBg">
          <h2 className="text-lg font-medium text-gray-900 dark:text-darkBg-mainText flex items-center">
            <Building2 className="h-6 w-6 mr-2 text-gray-500 dark:text-darkBg-mainText" />
            Community Information
          </h2>
        </div>
        <div className="p-4 sm:p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              {' '}
              <PolicyDetailsItem name="Name" value={policy?.community?.name ?? <ShowPendingInfo/>} />
              <PolicyDetailsItem
                name="Contact Person"
                value={policy?.community?.contactPersons[0]?.fullName ?? <ShowPendingInfo/>}
              />
              <PolicyDetailsItem name="Review Stage" value={policy?.community?.reviewStage ?? <ShowPendingInfo/>} />
              <PolicyDetailsItem
                name="Contact Person's Phone"
                value={
                  policy?.community?.contactPersons[0]?.phone
                    ? getPhoneNumberFromShortCodeFormat(policy?.community?.contactPersons[0]?.phone)
                    : <ShowPendingInfo/>
                }
              />
              <PolicyDetailsItem name="Type" value={policy?.community?.type ?? <ShowPendingInfo/>} />
              <PolicyDetailsItem
                name="Contact Person's Email"
                value={policy?.community?.contactPersons[0]?.email ?? '-'}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const getPolicyCreationOverview = () => {
    if ((user?.isRideSharing() || user?.isInsurer())) {
      return null;
    }

    return (
      <div className="bg-white dark:!text-darkBg-mainText dark:bg-darkBg dark:border dark:border-darkBg-border rounded-2xl shadow overflow-hidden">
        <div className="flex gap-2 flex-wrap items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b dark:border-b-darkBg-border border-gray-200 bg-gray-50 dark:bg-darkBg">
          <h2 className="text-lg font-medium text-gray-900 dark:text-darkBg-mainText flex items-center">
            <Shield className="h-6 w-6 mr-2 text-gray-500 dark:text-darkBg-mainText" />
            Policy Overview
          </h2>

          {policy?.insurerPolicyNumber && (
            <div className="flex justify-center gap-5">
              <div className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-gray-50 px-3 py-1.5 shadow-sm dark:border-darkBg-border dark:bg-darkBg">
                <span className="text-xs font-semibold tracking-wide text-gray-500 dark:text-darkBg-subText">
                  Policy No.
                </span>

                <span className="h-4 w-px bg-gray-200 dark:bg-darkBg-border" />

                <span className="text-sm font-semibold text-gray-900 dark:text-darkBg-mainText font-mono">
                  {policy.insurerPolicyNumber}
                </span>


              </div>

              <button
                onClick={() => policy && setIsDrawerOpen(!isDrawerOpen)}
                className="px-1.5 py-0.5 border-2 border-gray-600 dark:border-darkBg-mainText text-gray-700 dark:text-darkBg-mainText hover:bg-gray-50 dark:hover:bg-transparent text-xs font-medium rounded-full transition-all duration-200 flex items-center"
              >
                <Eye className="h-4 w-4 mr-1.5" />
                View Other Info.
              </button>
            </div>
          )}

        </div>

        <div className="p-4 sm:p-6">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">

            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-darkBg-subText">Date Created</p>
              <p className="mt-1 text-sm text-gray-900 dark:text-darkBg-mainText">{policy?.createdOn ? getFormattedDate({ date: policy.createdOn }) : <ShowPendingInfo/>}</p>
            </div>

            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-darkBg-subText">Policy Start Date</p>
              <p className="mt-1 text-sm text-gray-900 dark:text-darkBg-mainText">{policy?.startsOn ? getFormattedDate({ date: policy?.startsOn }) : <ShowPendingInfo/>}</p>
            </div>

            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-darkBg-subText">Policy End Date</p>
              <p className="mt-1 text-sm text-gray-900 dark:text-darkBg-mainText">{policy?.expiresOn ? getFormattedDate({ date: policy?.expiresOn }) : <ShowPendingInfo/>}</p>
            </div>


            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-darkBg-subText">Time to Policy Completion</p>
              <p className="mt-1 text-sm text-gray-900 capitalize dark:text-darkBg-mainText"> {policy?.metaData?.minutesToCompletion ? `${policy?.metaData?.minutesToCompletion} Minutes` : <ShowPendingInfo/>}</p>
            </div>

            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-darkBg-subText">{policy?.quote?.installmentType === INSTALLMENT_TYPES.FULL_PAYMENT ? 'Payment Received' : 'First Payment Received'}</p>
              <p className="mt-1 text-sm text-gray-900 capitalize dark:text-darkBg-mainText">{policy?.metaData?.firstPaymentReceived ? 'Yes' : 'No'}</p>
            </div>

            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-darkBg-subText">{policy?.quote?.installmentType === INSTALLMENT_TYPES.FULL_PAYMENT ? 'Payment Received On' : 'First Payment On'}</p>
              <p className="mt-1 text-sm text-gray-900 dark:text-darkBg-mainText">{policy?.metaData?.firstPaymentReceivedTime ? getFormattedDate({ date: policy?.metaData?.firstPaymentReceivedTime }) : <ShowPendingInfo/>}</p>
            </div>

            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-darkBg-subText">Vehicle Photos Received</p>
              <p className="mt-1 text-sm text-gray-900 capitalize dark:text-darkBg-mainText">{policy?.metaData?.vehiclePhotosReceived ? 'Yes' : 'No'}</p>
            </div>

            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-darkBg-subText">Policy Source</p>
              <p className="mt-1 text-sm text-gray-900 capitalize dark:text-darkBg-mainText">{policy?.quote?.source ? getFormattedLeadSource(policy?.quote?.source ?? '') : <ShowPendingInfo/>}</p>
            </div>

            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-darkBg-subText">Risk Score</p>
              <p className="mt-1 text-sm text-gray-900 dark:text-darkBg-mainText">-</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-darkBg-subText">Broker</p>
              <p className="mt-1 text-sm text-gray-900 dark:text-darkBg-mainText">{policy?.broker && policy?.broker?.name ? policy?.broker?.name : <ShowPendingInfo/>}</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const getVehicleInformation = (): JSX.Element => {
    return (
      <div className="bg-white rounded-2xl shadow overflow-hidden mb-6 dark:!text-darkBg-mainText dark:bg-darkBg dark:border dark:border-darkBg-border">
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b dark:border-b-darkBg-border border-gray-200 bg-gray-50 flex flex-wrap gap-2 items-center justify-between dark:bg-darkBg">
          <div className=" flex items-center">
            <div>
              <Car className="h-7 w-7 mr-2 text-gray-500 dark:text-darkBg-mainText" />
            </div>
            <div className='mr-2 flex items-center'>
              <h2 className='text-lg font-medium text-gray-900 dark:text-darkBg-mainText'>{vehicles && vehicles?.length <= 1 ? `Vehicle` : 'Vehicles'}</h2>
              {registrationNumbers && registrationNumbers.length > 0 && (
                <span className="ml-[2px] px-2 py-0.5 bg-gray-700 text-white text-xs font-bold rounded-full">
                  {registrationNumbers.length}
                </span>
              )}
            </div>

            {/* <SelectDropdown
              name="channel"
              onChange={(value: string) => {
                setselectedVehicleNumber(value)
              }}
              listSelectedValue={selectedVehicleNumber}
              list={registrationNumbers ? registrationNumbers.map((registrationNumber) => ({
                name: registrationNumber,
                value: registrationNumber
              })) : []
              }
              classes='!w-40'
              openModalAction={addNewVehicleModal}
              modalActionName='Add New Vehicle'
            /> */}
            <Button
              type="button"
              label="Add New Vehicle"
              bgColor="primary"
              fullWidth
              onClick={addNewVehicleModal}
            />
            {policy?.active &&
              <Tooltip message="Endorse Vehicle Information" placement="bottom">
                <div>
                  <Edit onClick={() => policy && selectedVehicleNumber && handleEditVehicleInformationModal(policy, 'Endorse Vehicle Information', true, false, false, false, false, selectedVehicleNumber)} className="h-4 w-4 ml-2 text-primary-main cursor-pointer" />
                </div>
              </Tooltip>
            }

          </div>
          <button
            onClick={() => policy && selectVehicleId && getPolicyVehiclePhotos(policy?.ref, Number(selectVehicleId))}
            className="px-1.5 py-0.5 border-2 border-gray-600 dark:border-darkBg-mainText text-gray-700 dark:text-darkBg-mainText hover:bg-gray-50 dark:hover:bg-transparent text-xs font-medium rounded-full transition-all duration-200 flex items-center"
          >
            <CameraIcon className="h-4 w-4 mr-1.5" />
            Request Vehicle Photos
          </button>

        </div>

        {vehicles && vehicles?.length > 1 && (
          <div className=" dark:!text-darkBg-mainText dark:bg-darkBg">
            {getVehicleTable()}
          </div>
        )}

        {policy?.vehicles && selectedVehicleNumber && policy?.vehicles?.filter((vehicle: Vehicle) => vehicle.registrationNumber === selectedVehicleNumber)?.map((vehicle: Vehicle) => (
          <div key={vehicle.registrationNumber} className="p-4 md:p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="col-span-1">
                {getVehicleSpecificationInfo(vehicle)}
              </div>
              <div className="col-span-1">
                {getVehicleRegistrationAndUsageInfo(vehicle)}
              </div>
            </div>
            {getOwnerAndValueInfo(vehicles)}


          </div>
        ))}

        {/* Vehicle Pictures Slideshow */}
        {policy && policy?.vehicle && policy?.vehicle?.images && policy?.vehicle?.images?.length > 0 && !user.isRideSharing() && (
          <div className="p-4 sm:p-6 border-b border-gray-200">
            <h3 className="text-md font-medium text-gray-900 mb-3 flex items-center">
              <BsImageFill className="h-4 w-4 mr-2 text-gray-500" />
              Vehicle Pictures
            </h3>

            <ImageGallery
              images={policy?.vehicle?.images || []}
            />
          </div>
        )}
      </div>
    );
  };

  const getInsuranceInformation = (): JSX.Element => {
    return (
      <div className="bg-white rounded-2xl shadow overflow-hidden dark:!text-darkBg-mainText dark:bg-darkBg dark:border dark:border-darkBg-border">
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b dark:border-b-darkBg-border border-gray-200 bg-gray-50 dark:bg-darkBg">
          <h2 className="text-lg font-medium text-gray-900 dark:text-darkBg-mainText flex items-center">
            <Building2 className="h-6 w-6 mr-2 text-gray-500 dark:text-darkBg-mainText flex items-center" />
            Insurance Information
            {policy?.active &&
              <Tooltip message="Endorse Insurance Information" placement="bottom">
                <div>
                  <Edit onClick={() => policy && handleEditVehicleInformationModal(policy, 'Endorse Insurance Information', false, false, false, true, false, selectedVehicleNumber)} className="h-4 w-4 ml-2 text-primary-main cursor-pointer" />
                </div>
              </Tooltip>
            }

          </h2>
        </div>
        <div className="p-4 sm:p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 ">

            {getProviderDetails()}
            {getPolicyTermsInformation()}

            {/* Other Info Section */}
            {getOtherInformation()}

            {/* Documents Section */}
            <div className="h-fit">
              {getPolicyDocumentsInfo()}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const getPaymentAnalysisInformation = (): JSX.Element => {
    return (
      <>
        {policy?.ref &&
          <div className='mt-5 hidden sm:block w-full'>
            <PaymentAnalysis
              paymentAnalysis={policy?.paymentAnalysis}
              financingApplicationNumber={Number(policy?.ref)}
              balance={balance()}
              handleViewFullPayment={scrollToPaymentHistory}
              handleRunPaymentAnalysis={() => runPaymentAnalysis(policy)}
              currency={policy?.currency}
            />
          </div>
        }
      </>
    )
  }

  const getCommissionSummary = () => {
    return (
      <div className="bg-white rounded-2xl shadow overflow-hidden dark:!text-darkBg-mainText dark:bg-darkBg dark:border dark:border-darkBg-border">
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b dark:border-b-darkBg-border border-gray-200 bg-gray-50 dark:bg-darkBg">
          <h2 className="text-lg font-medium text-gray-900 dark:text-darkBg-mainText flex items-center">
            {/* <File className="h-6 w-6 mr-2 text-gray-500 dark:text-darkBg-mainText" /> */}
            Commission Summary
          </h2>
          {settlement && !settlement.isPaid && <button
            onClick={() => settlement && handleCompleteSettlement(settlement, handleInvalidateCommissionsQueries)}
            className="px-3 py-1.5 border border-green-600  text-green-700 text-xs font-medium rounded-full transition-colors duration-200 flex items-center shadow-sm"
          >
            <CheckCircle className="h-4 w-4 mr-1.5" />
            Record Commission As Received
          </button>}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-2 gap-6 p-4 sm:p-6">
          <div className='flex flex-col gap-3'>
            <PolicyDetailsItem
              name="Gross premium"
              value={commission?.grossPremiumAmount ? formatCurrencyAmount(commission?.grossPremiumAmount) : <ShowPendingInfo/>}
            />
            <PolicyDetailsItem
              name="Net Premium"
              value={commission?.netPremiumAmount ? formatCurrencyAmount(commission?.netPremiumAmount) : <ShowPendingInfo/>}
            />
            <PolicyDetailsItem
              name="Gross com"
              value={commission?.grossCommissionAmount ? formatCurrencyAmount(commission?.grossCommissionAmount) : '0'}
            />
            <PolicyDetailsItem
              name="Amount Received"
              value={commission?.payout?.amountReceived ? formatCurrencyAmount(commission?.payout?.amountReceived) : <ShowPendingInfo/>}
            />
            <PolicyDetailsItem
              name="Balance Remaining"
              value={commission?.payout?.balanceRemaining ? formatCurrencyAmount(commission?.payout?.balanceRemaining) : '0'}
            />
          </div>
          <div className='flex flex-col gap-3'>
            <PolicyDetailsItem
              name="WHT"
              value={commission?.withholdingTax ? formatCurrencyAmount(commission?.withholdingTax) : <ShowPendingInfo/>}
            />
            <PolicyDetailsItem
              name="Net com"
              value={commission?.netCommissionAmount ? formatCurrencyAmount(commission?.netCommissionAmount) : <ShowPendingInfo/>}
            />
            <PolicyDetailsItem
              name="Com rate(%)"
              value={commission?.rate ? `${commission?.rate}%` : <ShowPendingInfo/>}
            />
            <PolicyDetailsItem
              name="Commission Reference"
              value={commission?.payout ?
                <div className='w-fit mt-1 text-sm text-blue-600 hover:text-blue-800 dark:text-darkBg-textLink dark:hover:text-darkBg-textLink font-medium hover:underline inline-flex items-center cursor-pointer'
                  onClick={() => navigate(user.isBroker() ? `${getDashboardBase()}/commissions/settlements/${commission?.payout?.id}` : `${getDashboardBase()}/settlements/${commission?.payout?.id}`)}>
                  {commission?.payout?.reference}
                  <LinkIcon className="h-3 w-3 ml-1" />
                </div> : <ShowPendingInfo/>}
            />
            <PolicyDetailsItem
              name="Payment status"
              value={
                commission?.payout ? commission?.payout?.isPaid
                  ?
                  <div className='flex items-center w-fit gap-2 bg-green-100 text-green-800 text-xs font-medium rounded-full px-2.5 py-0.5'>
                    <CheckCircle2 className='h-4 w-4 text-green-400' />
                    <span className=' font-semibold  truncate'>Settled</span>
                  </div>
                  :
                  <div className='flex items-center w-fit gap-2 bg-red-100 text-red-800 text-xs font-medium rounded-full px-2.5 py-0.5'>
                    <XCircleIcon className='h-4 w-4 text-red-400' />
                    <span className=' font-semibold  truncate'>Not Settled</span></div>

                  : <div>{<ShowPendingInfo/>}</div>}
            />
          </div>
        </div>
      </div>
    )
  }

  const getPremiumSummary = () => {
    return (
      <div className="bg-white rounded-2xl shadow overflow-hidden dark:!text-darkBg-mainText dark:bg-darkBg dark:border dark:border-darkBg-border">
        <div className="flex items-center px-4 sm:px-6 py-3 sm:py-4 border-b dark:border-b-darkBg-border border-gray-200 bg-gray-50 dark:bg-darkBg">
          <div className='flex items-center'>
            {/* <File className="h-6 w-6 mr-2 text-gray-500 dark:text-darkBg-mainText" /> */}
            <h2 className="text-lg font-medium text-gray-900 dark:text-darkBg-mainText flex items-center">Premium Summary</h2>
          </div>

          {/* {payout && !payout?.isPaid && <button
            onClick={() => payout && handleCompletePayout(payout, handleInvalidatePayoutsQueries)}
            className="px-3 py-1.5 border border-green-600  text-green-700 text-xs font-medium rounded-full transition-colors duration-200 flex items-center shadow-sm"
          >
            <CheckCircle className="h-4 w-4 mr-1.5" />
            Record Payout As Paid
          </button>} */}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-2 gap-6 p-4 sm:p-6">
          <div className='flex flex-col gap-3'>
            <PolicyDetailsItem
              name="Currency"
              value={policy?.currency ?? <ShowPendingInfo/>}
            />
            <PolicyDetailsItem
              name="Total Premium"
              value={policy?.quote?.paymentPlan?.totalPremium ? formatCurrencyAmount(policy?.quote?.paymentPlan?.totalPremium) : <ShowPendingInfo/>}
            />
            <PolicyDetailsItem
              name="Total Paid"
              value={policy?.totalPaid ? formatCurrencyAmount(policy?.totalPaid) : <ShowPendingInfo/>}
            />
            <PolicyDetailsItem
              name="Balance Remaining"
              value={policy?.balanceRemaining ? formatCurrencyAmount(policy?.balanceRemaining) : <ShowPendingInfo/>}
            />
            <PolicyDetailsItem
              name="Payout Reference"
              value={payout ?
                <div className='mt-1 text-sm text-blue-600 hover:text-blue-800 dark:text-darkBg-textLink dark:hover:text-darkBg-textLink font-medium hover:underline inline-flex items-center cursor-pointer' onClick={() => payout && navigate(`${getDashboardBase()}/payouts/${payout?.id}?type=premium`)}>
                  {payout?.reference}
                  <LinkIcon className="h-3 w-3 ml-1" />
                </div> : '-'}
            />
          </div>
          <div className='flex flex-col gap-3'>
            <PolicyDetailsItem
              name="Outright Premium"
              value={policy?.quote?.paymentPlan?.outRightPremium ? formatCurrencyAmount(policy?.quote?.paymentPlan?.outRightPremium) : <ShowPendingInfo/>}
            />
            <PolicyDetailsItem
              name="Discount Applied"
              value='NO'
            />
            <PolicyDetailsItem
              name="Payment Frequency"
              value={policy?.installmentType ? getInstallmentTypeFromKey(policy?.installmentType) : <ShowPendingInfo/>}
            />
            <PolicyDetailsItem
              name="Payout status"
              value={
                payout ? payout?.isPaid
                  ?
                  <div className='flex items-center w-fit gap-2 bg-green-100 text-green-800 text-xs font-medium rounded-full px-2.5 py-0.5'>
                    <CheckCircle2 className='h-4 w-4 text-green-400' />
                    <span className=' font-semibold  truncate'>Paid</span>
                  </div>
                  :
                  <div className='flex items-center w-fit gap-2 bg-red-100 text-red-800 text-xs font-medium rounded-full px-2.5 py-0.5'>
                    <XCircleIcon className='h-4 w-4 text-red-400' />
                    <span className=' font-semibold  truncate'>Unpaid</span></div>
                  : <div>{<ShowPendingInfo/>}</div>}
            />
          </div>
        </div>
      </div>
    )
  }

  const getFinancialInformation = (): JSX.Element => {
    return (
      <div className="bg-white rounded-2xl shadow overflow-hidden dark:!text-darkBg-mainText dark:bg-darkBg dark:border dark:border-darkBg-border">
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b dark:border-b-darkBg-border border-gray-200 bg-gray-50 dark:bg-darkBg">
          <h2 className="text-lg font-medium text-gray-900 dark:text-darkBg-mainText flex items-center">
            <Building2 className="h-6 w-6 mr-2 text-gray-500 dark:text-darkBg-mainText flex items-center" />
            Financial Information
          </h2>
          {!(settlement && payout) && <button
            onClick={resolveFinancial}
            className="px-3 py-1.5 border border-green-600  text-green-700 text-xs font-medium rounded-full transition-colors duration-200 flex items-center shadow-sm"
          >
            <CheckCircle className="h-4 w-4 mr-1.5" />
            Record Financials
          </button>}
        </div>
        <div className="p-4 sm:p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Premium Summary Section */}
            {getPremiumSummary()}

            {/* Commission Summary Section */}
            {getCommissionSummary()}
          </div>
        </div>
      </div>
    );
  };

  const handleTabClicked = (id: string) => {
    navigate(`${getDashboardBase()}/policies/${policy?.ref}?type=motor&tab=${id}`);
  };

  const getBackButtonLink = () => {
    navigate(-1)
  };

  const hasInsurerSubscribedToVehicleAnalysis = () => {
    return policy?.insurer?.products?.some((product: any) => product.name === PRODUCTS.AI_VEHICLE_VERIFICATION)
  }

  const getPolicyDocumentsInfo = () => {
    return (
      <DocumentDownloadCard
        title="Documents"
        documents={getPolicyDocuments()}
        openPictureModal={openPictureModal}
        uploadDoc={uploadDoc}
        sendAgreementFormToCustomer={() => policy && sendAgreementFormToCustomer(policy, true)}
      />
    )
  }

  const filteredVehicles = useMemo(() => {
    if (!searchQuery) return vehicles;

    const query = searchQuery.toLowerCase();
    return vehicles?.filter((vehicle: Vehicle) => {
      return (
        (vehicle.registrationNumber?.toLowerCase().includes(query))
      );
    });
  }, [vehicles, searchQuery]);

  const getVehicleTable = () => {
    return (

      <GTable
        columns={vehicleColumns}
        data={filteredVehicles ?? []}
        classes="!max-h-[300px] !mx-6 !py-3"
        searchClasses='!mx-6 !mt-3'
        searchPlaceholder='Search by registration number'
        onSearch={(searchValue) => { setSearchQuery(searchValue) }}
        showSearch
        tableType={'vehicles'}
        stickyHeader
      />

    )
  }


  return (
    <div className={clsx('rounded-lg p-4 min-h-full text-sm')}>
      {isLoading ? (
        <div className='h-screen flex items-center justify-center'>
          <Loader message='Loading policy details. Please wait...' brollyLoaderFixed />
        </div>
      ) : (
        <>
          {policy && (
            <div className="">

              {/* Policy Header */}
              <div className="flex flex-wrap w-full gap-2 justify-between  items-start mb-6 will-change-auto">
                <div className='flex justify-start gap-2'>
                  {hasPageHistory && <div className="w-fit hidden sm:block">
                    <BackButton onClick={getBackButtonLink} />
                  </div>
                  }
                  <div className="flex w-full flex-col items-center justify-start gap-2">
                    <div className="flex w-full max-[550px]:flex-col  gap-4">
                      <div className='flex gap-3 items-center justify-between w-full sm:justify-center'>
                        <div className='flex flex-col'>
                          <h2 className="text-2xl font-bold text-gray-900 mr-3 flex-shrink-0 dark:text-darkBg-mainText">
                            <span className="text-gray-500 dark:text-gray-400 font-medium">Policy Holder: </span>
                            {policy?.applicant?.fullName ?? <ShowPendingInfo message="Missing Policy Holder"/>}
                          </h2>
                          {policy?.insurerPolicyNumber && (
                            <span className="text-sm text-gray-500 dark:text-gray-400 mr-3">
                              Policy #: <span className="font-medium text-gray-700 dark:text-darkBg-mainText">{policy.insurerPolicyNumber}</span>
                            </span>
                          )}
                        </div>
                        <div className='hidden min-[680px]:block'>
                          <IconAction
                            messageIconOne={`Copy policy ID`}
                            IconOne={TbClipboardCopy}
                            onclickIconOne={async () => {
                              await navigator.clipboard.writeText(`${policy?.ref}`);
                            }}

                            messageIconTwo={`Copy policy link`}
                            IconTwo={IoIosLink}
                            onclickIconTwo={async () => {
                              await navigator.clipboard.writeText(
                                baseUrl +
                                '/admin' +
                                `/policies/${policy.ref}?type=motor&filter=All&page=0`
                              );
                            }}
                          />
                        </div>
                        <div className='block md:hidden'>
                          {(user.isOwner() || user.isSupervisor()) && (
                            <MdChangeCircle onClick={() => assignPolicyManager(policy)} className="size-5 bg-primary-main text-white flex items-center justify-center rounded-full p-1 cursor-pointer" />
                          )}
                        </div>
                      </div>
                      <div className='hidden md:flex gap-3 items-center justify-center'>
                        <div className="w-fit flex text-white">
                          <span
                            className={clsx('w-[30px] h-[30px] rounded-full flex items-center justify-center text-[0px] ', { ['bg-status-active']: policy.getStatus()?.toLowerCase() === POLICY_STATUS_OPTIONS.ACTIVE }, { ['bg-status-ended']: policy.getStatus()?.toLowerCase() === POLICY_STATUS_OPTIONS.INACTIVE })}>
                            {policy.getStatus()}
                          </span>
                        </div>
                        <PolicyStatus
                          status={reviewStageDisplay}
                          classes="rounded-full px-2  w-fit"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Actions Button */}
                <div className="flex flex-row space-x-2 items-center md:mt-0 justify-between">
                  {/* <div className='flex items-center pt-2 md:hidden'>
                    {getPolicyActions()?.length > 0 &&
                      <PopOnMe actions={getPolicyActions()}>
                        <EllipsisVerticalIcon className="h-6 w-6" />
                      </PopOnMe>
                    }
                  </div> */}
                  <div className=" flex items-center justify-center gap-x-3 mb-2 min-[400px]:mb-0">
                    <div className='flex md:hidden gap-3 items-center justify-center'>
                      <div className="w-fit flex text-white">
                        <span
                          className={clsx('w-[30px] h-[30px] rounded-full flex items-center justify-center text-[0px] ', { ['bg-status-active']: policy.getStatus()?.toLowerCase() === POLICY_STATUS_OPTIONS.ACTIVE }, { ['bg-status-ended']: policy.getStatus()?.toLowerCase() === POLICY_STATUS_OPTIONS.INACTIVE })}>
                          {policy.getStatus()}
                        </span>
                      </div>
                      <PolicyStatus
                        status={capitalizeSentence(policy?.reviewStage ?? '-')}
                        classes="rounded-full px-2  w-fit"
                      />
                    </div>
                    {policy &&
                      (user.isOwner() && policy?.manager ? (
                        <span className="flex items-center gap-1 whitespace-nowrap text-sm">
                          <span className="text-gray-500 dark:text-gray-400">Account Officer:</span>
                          <Link to={`${getDashboardBase()}/teams/staff/${policy?.manager?.id}`} className="hover:underline underline-offset-2 text-blue-600 dark:text-darkBg-textLink dark:hover:text-darkBg-textLink">
                            {`${policy.manager?.firstName} ${policy.manager?.lastName}`}
                          </Link>
                        </span>
                      ) : (
                        <>
                          {policy.manager &&
                            <Tooltip message="Account Officer" placement="left">
                              <div className="flex items-center gap-1 text-sm whitespace-nowrap">
                                <span className="text-gray-500 dark:text-gray-400">Account Officer:</span>
                                <span className="font-semibold dark:text-primary-main">
                                  {policy.manager?.firstName} {policy.manager?.lastName}
                                </span>
                              </div>
                            </Tooltip>
                          }
                        </>
                      ))}
                    <div className=''>
                      {(user.isOwner() || user.isSupervisor()) && (
                        <Tooltip message="Assign policy to manager" placement="bottom">
                          <div>
                            <MdChangeCircle className="size-5 bg-primary-main text-white flex items-center justify-center rounded-full p-1 cursor-pointer" onClick={() => assignPolicyManager(policy)} />
                          </div>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                  <div className='hidden md:block'>
                    {getPolicyActions()?.length > 0 && (
                      <ActionsPopOnMe actions={getPolicyActions()} popOptionClasses="" />
                    )}
                  </div>
                  <div className='flex items-center md:hidden'>
                    {getPolicyActions()?.length > 0 &&
                      <PopOnMe actions={getPolicyActions()}>
                        <EllipsisVerticalIcon className="h-6 w-6" />
                      </PopOnMe>
                    }
                  </div>
                </div>
              </div>

              {/* Pending Requirements banner — surfaces what's blocking the policy from moving to the insurer */}
              {!policy?.active && hasBlockers && (
                <div className="py-6 flex justify-center">
                  <div className="w-full lg:w-2/3 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/40 dark:border-amber-700 p-4">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="h-5 w-5 text-amber-700 dark:text-amber-300 mt-0.5 shrink-0" />
                      <div className="flex-1">
                        <h4 className="text-sm font-semibold text-amber-900 dark:text-amber-100">
                          Pending Requirements
                        </h4>
                        <p className="text-xs text-amber-800 dark:text-amber-200 mt-1">
                          This policy can&apos;t move to the insurer until the items below are resolved.
                        </p>
                        <ul className="mt-3 space-y-1.5">
                          {blockers.map((b) => (
                            <li key={b.code} className="flex items-start gap-2 text-sm text-amber-900 dark:text-amber-100">
                              <XCircle className="h-4 w-4 text-amber-700 dark:text-amber-300 mt-0.5 shrink-0" />
                              <span>
                                <span className="font-medium">{b.label}</span>
                                <span className="text-amber-800 dark:text-amber-200"> — {b.description}</span>
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <button
                        type="button"
                        disabled={isApproving}
                        onClick={() => {
                          setIsApproving(true);
                          handleApprovePolicy(policy as Policy, async () => {
                            await refreshBlockers();
                            setIsApproving(false);
                          });
                        }}
                        className="!bg-amber-600 hover:!bg-amber-700 disabled:opacity-60 text-white text-sm font-medium px-4 py-2 rounded-md whitespace-nowrap"
                      >
                        {isApproving ? 'Updating…' : 'Update'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Alert Message */}
              {!policy?.active && !hasBlockers && (
                <div className="py-8 flex justify-center">
                  {policy.reviewStage === POLICY_REVIEW_STATES.IN_APPROVAL &&
                    policy.updates.length > 0 ? (
                    <MessagePrompt type={'error'} message={getLatestInApprovalUpdate()?.description} classes="w-full lg:w-2/3" />
                  ) : (
                    <MessagePrompt
                      type={'info'}
                      message={'Kindly ensure all required information for policy have been provided for, then mark each section as checked to be able to submit the policy for approval and have the sticker issued.'}
                      classes="w-full lg:w-2/3"
                    />
                  )}
                </div>
              )}

              {/* endorsement banner */}
              {
                <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 mb-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="bg-gray-100 dark:bg-gray-700 rounded-full p-2">
                        <Edit className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                      </div>
                      <div>
                        {!policy?.active && <h3 className="text-sm font-medium text-gray-900 dark:text-white">Policy Not Active</h3>}

                        {!policy?.active ? <p className="text-sm text-gray-600 dark:text-gray-400">Please note that only an active policy can apply endorsement by clicking on the yellow icon <span className='inline-block relative top-[2px]'><Edit className="h-4 w-4 text-primary-main" /></span> if available.</p>
                          : <p className="text-sm text-gray-600 dark:text-gray-400">Please note that you can apply an endorsement by clicking on the yellow icon <span className='inline-block relative top-[2px]'><Edit className="h-4 w-4 text-primary-main" /></span> below.</p>
                        }
                      </div>
                    </div>
                  </div>
                </div>
              }

              {/* Consolidated Action Banner */}
              {(!(policy?.dataComplete && policy?.active) || !policy?.isInitialPaymentReceived()) && (
                <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 mb-6">
                  {/* Policy Information Incomplete */}
                  {!(policy?.dataComplete && policy?.active) && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="bg-gray-100 dark:bg-gray-700 rounded-full p-2">
                          <Edit2 className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                        </div>
                        <div>
                          <h3 className="text-sm font-medium text-gray-900 dark:text-white">Policy Information Incomplete</h3>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Please complete all required policy information</p>
                        </div>
                      </div>
                      <button
                        onClick={() => navigate(`${getDashboardBase()}/policies/${policy?.ref}/edit?type=motor`)}
                        className="bg-gray-900 dark:bg-white text-white dark:text-gray-900 px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
                      >
                        Update Policy information
                      </button>
                    </div>
                  )}

                  {/* Payment Required - Show with divider if both conditions are met */}
                  {!policy?.isInitialPaymentReceived() && (
                    <div className={`${!(policy?.dataComplete && policy?.active) ? 'mt-4 pt-4 border-t border-gray-200 dark:border-gray-700' : ''}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="bg-gray-100 dark:bg-gray-700 rounded-full p-2">
                            <CreditCard className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                          </div>
                          <div>
                            <h3 className="text-sm font-medium text-gray-900 dark:text-white">Payment Required</h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400">Please make the initial payment to activate your policy</p>
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            const paymentSection = document.getElementById('payments-tab-content');
                            if (paymentSection) {
                              paymentSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }
                          }}
                          className="bg-gray-900 dark:bg-white text-white dark:text-gray-900 px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
                        >
                          Make Payment
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Main Content - Two Column Layout */}
              <div className=" mb-5">

                {/* Left Column - 2/3 width */}
                <div className="lg:col-span-2 space-y-6">
                  {/* Policy Overview Section */}
                  {getPolicyCreationOverview()}

                  {/* Vehicle Information Section */}
                  {getVehicleInformation()}

                  {/* Vehicle Analysis */}
                  {policy?.vehicle?.images && policy?.vehicle?.images?.length > 0 && hasInsurerSubscribedToVehicleAnalysis() && (
                    <div className="">
                      <VehicleAnalysisCard vehicle={policy?.vehicle} onRefresh={onRefreshAnalysis} />
                    </div>
                  )}

                  {/* Plan Section */}
                  {/* {getPlanDetails()} */}

                  {/* Insurance Information Section */}
                  {getInsuranceInformation()}

                  {/* Third Party Information Section */}
                  <div className={`grid grid-cols-1 md:grid-cols-2 gap-5 ${policy && policy.community ? '' : 'hidden'}`}>
                    {/* Community Information Section */}
                    {policy && policy.community && getCommunityDetails()}
                  </div>

                  {getFinancialInformation()}
                </div>
                {/* Right Column - 1/3 width */}

              </div>


              {/* Tabbed Section for Tasks, Claims, and Payments */}
              <div className="bg-white rounded-2xl shadow overflow-hidden mb-6 dark:!text-darkBg-mainText dark:bg-darkBg dark:border dark:border-darkBg-border">
                <div className="px-4 sm:px-6 py-3 sm:py-4 border-b dark:border-darkBg-border border-gray-200  bg-gray-50 dark:bg-darkBg">
                  <h2 className="text-lg font-medium text-gray-900 dark:text-darkBg-mainText flex items-center">
                    <Table className="h-6 w-6 mr-2 text-gray-500 dark:text-darkBg-mainText" />
                    {sentenceCase(activeTab)} Records
                  </h2>
                </div>

                {/* Tab Navigation */}
                {/* {(!user.isInsurer() || user.isInsurerPolicyAdmin()) && */}
                <div className="flex items-center gap-5 border-b border-gray-200 dark:border-darkBg-border">
                  <TabNav
                    tabs={getTabs()}
                    activeTab={activeTab}
                    onTabClicked={handleTabClicked}
                    setActiveTab={(id: string) => {
                      setActiveTab(id);
                    }}
                  />
                </div>
                {/* } */}

                <div className="p-3" ref={pageRef} >

                  {/* Payments Tab */}
                  <TabContent id={'payments'} activeTab={activeTab}>
                    <div id="payments-tab-content">
                      {!user.isRideSharing() && (
                        // {(!user.isInsurer() || user.isInsurerPolicyAdmin()) && !user.isRideSharing() && (
                        <div className="">
                          <div className="px-3 sm:px-5 grid grid-cols-1 min-[380px]:grid-cols-2 md:grid-cols-4 gap-3 place-items-start md:place-content-center md:place-items-center lg:place-items-baseline">
                            <div className='flex flex-col'>
                              <span className="font-bold">Total Premium:</span>{' '}
                              <span className="px-2">
                                {policy?.quote?.paymentPlan?.totalPremium?.toFixed(2)}
                              </span>
                            </div>
                            <div className='flex flex-col'>
                              <span className="font-bold">Total Paid:</span>{' '}
                              <span className="px-2">{policy?.totalPaid?.toFixed(2)}</span>
                            </div>
                            <div className='flex flex-col whitespace-nowrap'>
                              <span className="font-bold">Balance:</span>{' '}
                              <span className="px-2">{policy?.balanceRemaining?.toFixed(2)}</span>
                            </div>
                            <div className="flex flex-col ">
                              <span className="font-bold">Discount Applied:</span>{' '}
                              <div className="px-2">{policy?.discount ? 'YES' : 'NO'}</div>
                              <div className="mt-1">
                                {policy?.discount && (
                                  <Tooltip placement="bottom" bgColor="bg-gray-200" message={'View discount info'}>
                                    <PopOver trigger={<FaEye size={16} />} closeOnOutsideClick={true}>
                                      <DiscountInfoModal
                                        code={policy?.discount?.code}
                                        name={policy?.discount?.name}
                                        value={policy?.discount?.value}
                                        type={policy?.discount?.type}
                                      />
                                    </PopOver>
                                  </Tooltip>
                                )}
                              </div>
                            </div>
                            {/* <div className="flex justify-start md:items-center md:justify-end ">
                            {getPaymentActions()?.length > 0 && (
                              <ActionsPopOnMe text="Payment Actions" actions={getPaymentActions()} popOptionClasses='' />
                            )}
                          </div> */}
                          </div>
                          <div className="overflow-x-auto">
                            <GTable
                              showCheckboxes
                              selectionChanged={(selectedObjects: any[], selectedIndexes: number[]) => {
                                const paymentIdList = selectedObjects.map((payment) => payment.id);
                                setSelectedPayments(paymentIdList);
                              }}
                              stickyHeader
                              classes='!max-h-[400px]'
                              columns={screenWidth < 550 ? mobileColumns : columns}
                              data={policy?.payments?.sort((a, b) => {
                                const dateA = a.dueDate ? new Date(a.dueDate).getTime() : 0;
                                const dateB = b.dueDate ? new Date(b.dueDate).getTime() : 0;
                                return dateA - dateB;
                              }) || []}
                              tableType="payment"
                              showSearch={false}
                              loading={loading}
                              tableContainerId="tableId"
                            // headerClasses='first:hidden py-[0.5rem] text-left border-b-[1px] border-gray-300 rounded'
                            />
                          </div>
                          {/* </div> */}
                        </div>
                      )}
                    </div>
                  </TabContent>

                  {/* Claims Tab */}
                  <TabContent id={'claims'} activeTab={activeTab}>
                    {/* {!(user.isInsurer()) && ( */}
                    <div className=" my-5">
                      <div className="overflow-x-auto">
                        <GTable
                          columns={claimsColumns}
                          data={claims}
                          classes="h-fit"
                          showSearch={false}
                          tableType={'claim'}
                          onRowClick={(claim: Claim) => {
                            navigate(`${getDashboardBase()}/claims/${claim.claimNumber}`, {
                              replace: true
                            });
                          }}
                          loading={claimsLoading}
                          stickyHeader
                        />
                      </div>
                    </div>
                    {/* )} */}
                  </TabContent>
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex py-4 justify-end">
                {getButtonActions()?.length > 0 && (
                  <ActionsPopOnMe actions={getButtonActions()} showDropdown={true} />
                )}
              </div>
            </div>
          )}

          {!policy && (
            <div className="h-screen flex items-center justify-center w-full">
              <NotFoundComponent text="Policy not found" />{' '}
            </div>
          )}


          {policy && showUpdates && (
            <PolicyUpdates
              show={showUpdates}
              onClose={() => {
                setShowUpdates(false);
              }}
              onProceed={() => { }}
              updates={policy?.updates ?? []}
              policyId={policy && policy.ref}
              onPolicyUpdated={(policy: Policy) => setPolicy(policy)}
            />
          )}

          <ModalComponent />
          <PolicyModalComponent />
          <PaymentModalComponent />
          <PayoutModal />
          <SettlementModal />
          <ClaimModal />
          <PolicyDetailsDrawerContent
            policy={policy}
            getOtherInformation={getOtherInformation}
            getPaymentAnalysisInformation={getPaymentAnalysisInformation}
            getRefereeDetails={getRefereeDetails}
            getPolicyDocumentsInfo={getPolicyDocumentsInfo}
            onSetShowUpdates={(state) => setShowUpdates(state)}
            isOpen={isDrawerOpen}
            onToggle={() => setIsDrawerOpen(!isDrawerOpen)}
          />
        </>
      )
      }
    </div >
  );
};

export const getStatus = (status: string) => {
  return status ? (
    <div
      className={`text-xs  w-fit px-5 flex items-center justify-center rounded ${SUCCESS_PAYMENT_STATUS_COLOR}`}>
      Done
    </div>
  ) : (
    <div className="text-xs  w-fit px-5 flex items-center justify-center rounded bg-gray-500 text-white">
      TO DO
    </div>
  );
};

type PolicyDetailsItemProps = {
  name: string | number | JSX.Element;
  value?: string | number | JSX.Element;
  append?: React.ReactNode;
  prepend?: React.ReactNode;
};

const PolicyDetailsItem = ({ name, value, append, prepend }: PolicyDetailsItemProps) => {
  // `value` is typed as JSX.Element so callers can pass tooltips, badges, or
  // other block-level fragments. Rendering those inside a <p> produces invalid
  // HTML (<p> can only contain phrasing content), so the value wrappers are
  // <div>s — the typography classes apply unchanged.
  return (
    <div>
      <p className="text-sm font-medium text-gray-500 dark:text-darkBg-subText">{name}</p>
      {prepend || append ? (
        <div className="flex items-center gap-1 mt-1">
          {prepend}
          <div className="truncate whitespace-nowrap text-sm text-gray-900 dark:text-darkBg-mainText capitalize">{value}</div>
          {append}
        </div>
      ) : (
        <div className="mt-1 text-sm text-gray-900 dark:text-darkBg-mainText capitalize">{value}</div>
      )}
    </div>
  );
};

export default PolicyDetails;
